/**
 * The one interface between the simulation and any renderer.
 *
 * ADR-0002 commits to keeping the simulation engine-agnostic so that re-hosting
 * the client (in a native engine, or anywhere else) stays a contained port
 * rather than a rewrite. This file is where that commitment is enforced:
 * commands go in, immutable snapshots come out, and no view code is permitted
 * to reach into simulation internals.
 *
 * If a renderer needs something, it gets a field on a snapshot - not a
 * reference to a system.
 */
import { Simulation } from '../Simulation.js';
import type {
  GameState, Lead, CourierJob, Customer, WorkTaskKind,
} from '../state/GameState.js';
import { BusinessSystem } from '../systems/BusinessSystem.js';
import type { SimEvent, DaySummary } from '../core/events.js';
import { BALANCE } from '../data/balance.js';
import {
  traitsOf, relationshipOf, tierOf, districtBlockedBy,
} from '../systems/CustomerSystem.js';
import { EQUIPMENT, EMPLOYEE_KIT, VEHICLES, HOUSING, FOOD, getEquipment } from '../data/catalog.js';
import { getLocation, distanceBetween, type WorldLocation } from '../data/world.js';
import { WEATHER_LABELS, type Weather } from '../systems/WeatherSystem.js';
import { formatMoneyShort } from '../core/money.js';
import {
  SaveSystem, type SaveStorage, type SaveMeta, MemoryStorage, AUTOSAVE_SLOT,
} from '../systems/SaveSystem.js';
import type { Applicant } from '../systems/EmployeeSystem.js';

export interface HudSnapshot {
  day: number;
  dayName: string;
  clock: string;
  hour: number;
  speed: number;
  paused: boolean;
  cash: number;
  bank: number;
  debt: number;
  netWorth: number;
  reputation: number;
  hunger: number;
  energy: number;
  businessName: string;
  registered: boolean;
  employees: number;
  openJobs: number;
  newLeads: number;
  goalProgress: number;
  nextMilestone: string | null;
  weather: Weather;
  weatherLabel: string;
  activeWork: {
    title: string;
    progress: number;
    minutesRemaining: number;
    /** How much of the whole job is done, across every task, 0..1. */
    coverage: number;
    /** The parts of this job, so the HUD can show what is left. */
    tasks: { kind: WorkTaskKind; weight: number; progress: number }[];
  } | null;
}

export interface LeadView {
  id: string;
  customerName: string;
  locationId: string;
  locationName: string;
  lotSize: 'small' | 'medium' | 'large';
  payout: number;
  estimatedMinutes: number;
  distanceMetres: number;
  ratePerHour: number;
  accepted: boolean;
  fromContract: boolean;
  assigned: boolean;
  hoursUntilExpiry: number;
  /**
   * What the customer is like, in words rather than numbers.
   *
   * Traits are only worth having if the player can read them before
   * accepting; a hidden difficulty modifier is just noise in the payout. Two
   * short tags is the most a job card can carry without becoming a spec
   * sheet, so only traits far enough from average to matter are named.
   */
  tags: string[];
  /** How well this household knows the player. */
  relationship: 'new' | 'returning' | 'regular' | 'loyal';
  /** Jobs already done for this household. */
  jobsForCustomer: number;
  /** The district the property is in, so travel reads as a choice. */
  tier: 'starter' | 'middle' | 'wealthy';
}

export interface GigView {
  id: string;
  title: string;
  pickupName: string;
  dropoffName: string;
  pickupId: string;
  dropoffId: string;
  pay: number;
}

export interface ShopItemView {
  id: string;
  name: string;
  price: number;
  description: string;
  affordable: boolean;
  locked: boolean;
  lockReason: string | null;
  owned: boolean;
  speedGain: number;
  qualityGain: number;
  /** Condition of an owned tool, 0..1. Null for anything not owned. */
  condition: number | null;
  /** What a service costs right now. 0 when there is nothing to do. */
  serviceCost: number;
  /** Whether the shop should be shouting about it. */
  needsService: boolean;
}

export interface EmployeeView {
  id: string;
  name: string;
  hourlyWage: number;
  skill: number;
  jobsCompleted: number;
  accruedWages: number;
  absentToday: boolean;
}

export type InteractionKind =
  | 'sleep' | 'shop_tools' | 'shop_food' | 'bank' | 'depot' | 'job_site' | 'none';

export interface InteractionView {
  kind: InteractionKind;
  locationId: string;
  label: string;
  /** Only for job sites. */
  leadId?: string;
}

function formatShort(cents: number): string {
  return formatMoneyShort(cents);
}

function nameOfLocation(id: string): string {
  return getLocation(id).name;
}

export class GameFacade {
  sim: Simulation;
  private saves: SaveSystem;
  private listeners = new Set<(event: SimEvent) => void>();
  /**
   * The renderer owns the avatar's position and writes it back every frame, so
   * anything that moves the player from the simulation side has to tell the
   * renderer or it is silently overwritten. This is that channel.
   */
  private teleportListeners = new Set<
    (x: number, z: number, outwardX: number, outwardZ: number) => void
  >();
  private applicantCache: Applicant[] | null = null;
  private applicantsForDay = -1;

  constructor(storage?: SaveStorage, seed?: number) {
    this.sim = new Simulation(seed !== undefined ? { seed } : {});
    this.saves = new SaveSystem(storage ?? new MemoryStorage());
    this.sim.bus.subscribe((e) => {
      for (const l of this.listeners) l(e);
    });
  }

  onEvent(listener: (event: SimEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onTeleport(
    listener: (x: number, z: number, outwardX: number, outwardZ: number) => void,
  ): () => void {
    this.teleportListeners.add(listener);
    return () => this.teleportListeners.delete(listener);
  }

  /** Move the player to a location, consuming travel time, and move the avatar. */
  travelTo(locationId: string): void {
    this.sim.travelTo(locationId);
    const p = this.state.player;
    // The entry offset points away from the building, which is the side the
    // camera wants to be on.
    const loc = getLocation(locationId);
    for (const l of this.teleportListeners) l(p.x, p.z, loc.entryX, loc.entryZ);
  }

  private get state(): GameState {
    return this.sim.state;
  }

  // --- time ---------------------------------------------------------------

  tick(realSeconds: number): void {
    this.sim.tickRealSeconds(realSeconds);
  }

  setSpeed(speed: number): void {
    this.sim.time.setSpeed(this.state, speed);
  }

  togglePause(): void {
    this.sim.time.togglePause(this.state);
  }

  sleep(): void {
    this.sim.sleep();
  }

  // --- snapshots ----------------------------------------------------------

  hud(): HudSnapshot {
    const s = this.state;
    const work = s.player.activeWork;
    const next = this.sim.milestones.next(s);
    return {
      day: s.time.day,
      dayName: this.sim.time.dayName(s),
      clock: this.sim.time.formatClock(s),
      hour: this.sim.time.hour(s),
      speed: s.time.speed,
      paused: s.time.paused,
      cash: s.finance.cash,
      bank: s.finance.bank,
      debt: s.finance.debt,
      netWorth: this.sim.netWorth(),
      reputation: s.player.reputation,
      hunger: s.player.needs.hunger.value,
      energy: s.player.needs.energy.value,
      businessName: s.business.registered ? s.business.name : 'No business yet',
      registered: s.business.registered,
      employees: s.business.employees.length,
      openJobs: this.sim.openWork().length,
      newLeads: s.leads.filter((l) => !l.accepted).length,
      weather: this.sim.weather.current(s),
      weatherLabel: WEATHER_LABELS[this.sim.weather.current(s)],
      goalProgress: Math.max(0, Math.min(1, this.sim.netWorth() / BALANCE.netWorthGoal)),
      nextMilestone: next?.label ?? null,
      activeWork: work
        ? {
            title: work.title,
            progress: Math.min(1, work.elapsedMinutes / work.totalMinutes),
            minutesRemaining: Math.max(0, Math.ceil(work.totalMinutes - work.elapsedMinutes)),
            coverage: work.coverage,
            tasks: work.tasks.map((t) => ({ ...t })),
          }
        : null,
    };
  }

  leads(): LeadView[] {
    const s = this.state;
    const from = s.player.atLocationId ?? 'home';
    return s.leads.map((lead) => this.toLeadView(lead, from));
  }

  private toLeadView(lead: Lead, fromLocationId: string): LeadView {
    const s = this.state;
    const minutes = this.sim.business.canWork(s)
      ? this.sim.business.workMinutesFor(s, lead, this.sim.needs.performanceMultiplier(s))
      : BALANCE.yard.minutesBySize[lead.lotSize];
    const distance = distanceBetween(fromLocationId, lead.locationId);
    const travel = this.sim.property.travelMinutes(s, distance);
    const customer = s.customers.find((c) => c.id === lead.customerId);
    return {
      id: lead.id,
      customerName: lead.customerName,
      locationId: lead.locationId,
      locationName: getLocation(lead.locationId).name,
      lotSize: lead.lotSize,
      payout: lead.payout,
      estimatedMinutes: minutes,
      distanceMetres: Math.round(distance),
      ratePerHour: Math.round((lead.payout / (minutes + travel)) * 60),
      accepted: lead.accepted,
      fromContract: lead.fromContract,
      assigned: lead.assignedEmployeeId !== null,
      hoursUntilExpiry: Math.max(
        0,
        Math.round((lead.expiresAtMinute - s.time.totalMinutes) / 60),
      ),
      tags: leadTags(s.customers.find((c) => c.id === lead.customerId)),
      relationship: customer ? relationshipOf(customer) : 'new',
      jobsForCustomer: customer?.jobsCompleted ?? 0,
      tier: tierOf(lead.locationId),
    };
  }

  /**
   * The districts, and what is standing between the player and each of them.
   *
   * Exists so the phone can say which of the two locks is on. A lead board
   * that quietly stops growing gives the player no way to tell whether they
   * need a better name or a better vehicle, and guessing wrong costs days -
   * the difference between grinding reputation and saving $900.
   */
  districts(): {
    tier: 'starter' | 'middle' | 'wealthy';
    name: string;
    open: boolean;
    blockedBy: 'reputation' | 'vehicle' | null;
    needReputation: number;
    needVehicle: 'none' | 'any' | 'car';
  }[] {
    const named = [
      ['starter', 'Maple Grove'],
      ['middle', 'Fairview and Linden'],
      ['wealthy', 'Hillcrest'],
    ] as const;
    return named.map(([tier, name]) => {
      const blockedBy = districtBlockedBy(tier, this.state);
      return {
        tier,
        name,
        open: blockedBy === null,
        blockedBy,
        needReputation: BALANCE.yard.tierMinReputation[tier],
        needVehicle: BALANCE.yard.tierMinVehicle[tier],
      };
    });
  }

  gigs(): GigView[] {
    return this.state.courierJobs.map((job: CourierJob) => ({
      id: job.id,
      title: job.title,
      pickupId: job.pickupId,
      dropoffId: job.dropoffId,
      pickupName: getLocation(job.pickupId).name,
      dropoffName: getLocation(job.dropoffId).name,
      pay: job.pay,
    }));
  }

  toolShop(): ShopItemView[] {
    const s = this.state;
    const owned = new Set(s.business.equipment);
    const items: ShopItemView[] = EQUIPMENT.map((def) => {
      const locked = !!def.requiresReputation && s.player.reputation < def.requiresReputation;
      const best = this.sim.business.bestInSlot(s, def.slot);
      return {
        id: def.id,
        name: def.name,
        price: def.price,
        description: def.description,
        affordable: s.finance.cash >= def.price,
        locked,
        lockReason: locked ? `Needs ${def.requiresReputation} reputation` : null,
        owned: owned.has(def.id),
        speedGain: best ? def.speed - best.speed : def.speed - 1,
        qualityGain: def.quality,
        condition: owned.has(def.id) ? BusinessSystem.conditionOf(s, def.id) : null,
        serviceCost: owned.has(def.id) ? this.sim.business.serviceCost(s, def.id) : 0,
        needsService: owned.has(def.id)
          && BusinessSystem.conditionOf(s, def.id) < BALANCE.equipment.warnBelow,
      };
    });
    items.push({
      id: EMPLOYEE_KIT.id,
      name: EMPLOYEE_KIT.name,
      price: EMPLOYEE_KIT.price,
      description: EMPLOYEE_KIT.description,
      affordable: s.finance.cash >= EMPLOYEE_KIT.price,
      locked: !s.business.registered,
      lockReason: s.business.registered ? null : 'Register your business first',
      owned: false,
      speedGain: 0,
      qualityGain: 0,
      condition: null,
      serviceCost: 0,
      needsService: false,
    });
    return items;
  }

  vehicles() {
    const s = this.state;
    return VEHICLES.map((v) => ({
      id: v.id,
      name: v.name,
      price: v.price,
      description: v.description,
      owned: s.player.vehicleId === v.id,
      affordable: s.finance.cash >= v.price,
    }));
  }

  housingOptions() {
    const s = this.state;
    return HOUSING.map((h) => ({
      id: h.id,
      name: h.name,
      deposit: h.deposit,
      weeklyRent: h.weeklyRent,
      description: h.description,
      current: s.player.housingId === h.id,
      affordable: s.finance.cash >= h.deposit,
    }));
  }

  food() {
    return FOOD.map((f) => ({
      id: f.id,
      name: f.name,
      price: f.price,
      hunger: f.hunger,
      energy: f.energy,
      affordable: this.state.finance.cash >= f.price,
    }));
  }

  employees(): EmployeeView[] {
    return this.state.business.employees.map((e) => ({
      id: e.id,
      name: e.name,
      hourlyWage: e.hourlyWage,
      skill: e.skill,
      jobsCompleted: e.jobsCompleted,
      accruedWages: e.accruedWages,
      absentToday: e.absentToday,
    }));
  }

  /** Applicants are stable within a day, so the list does not reshuffle. */
  applicants(): Applicant[] {
    if (this.applicantsForDay !== this.state.time.day || !this.applicantCache) {
      this.applicantsForDay = this.state.time.day;
      this.applicantCache = this.sim.employees.generateApplicants(this.state, 3);
    }
    return this.applicantCache;
  }

  transactions(limit = 25) {
    return this.state.finance.transactions.slice(-limit).reverse();
  }

  milestones() {
    return (BALANCE.milestones as readonly { id: string; label: string }[]).map((m) => ({
      id: m.id,
      label: m.label,
      done: this.state.milestones.includes(m.id),
    }));
  }

  businessSummary() {
    const s = this.state;
    return {
      registered: s.business.registered,
      name: s.business.name,
      equipmentCount: s.business.equipment.length,
      equipmentNames: s.business.equipment.map((id) => getEquipment(id).name),
      crewKits: s.business.crewKits,
      speedMultiplier: this.sim.business.speedMultiplier(s),
      qualityBase: this.sim.business.qualityBase(s),
      weeklyWageBill: this.sim.employees.weeklyWageBill(s),
      weeklyExpenses: this.sim.expenses.weeklyCommitment(s),
      lifetimeRevenue: s.business.lifetimeRevenue,
      canRegister: this.sim.business.canRegister(s),
      registrationFee: BALANCE.business.registrationFee,
      registrationMinReputation: BALANCE.business.registrationMinReputation,
      hireCheck: this.sim.employees.canHire(s),
    };
  }

  /**
   * The single thing the player should do next.
   *
   * This is the onboarding. The brief rules out a tutorial with walls of text,
   * so the game has to teach itself: at any moment there is exactly one
   * objective on screen, it names a place or an action, and it changes the
   * moment the player does it. Following it from a cold start walks you through
   * earning, spending, working, reputation, registering and hiring without ever
   * being told that is what is happening.
   */
  nextObjective(): {
    id: string;
    label: string;
    hint: string;
    progress: number;
    now: string;
    target: string;
  } {
    const s = this.state;
    const cash = s.finance.cash;
    const mowerPrice = 6000;
    const hasMower = this.sim.business.canWork(s);
    const accepted = this.sim.openWork();
    const atLocation = s.player.atLocationId;

    if (s.player.activeWork) {
      const w = s.player.activeWork;
      return {
        id: 'finish_job',
        label: 'Finish the job',
        hint: 'Stay on the property. Speed up time if you like.',
        progress: Math.min(1, w.elapsedMinutes / w.totalMinutes),
        now: `${Math.max(0, Math.ceil(w.totalMinutes - w.elapsedMinutes))} min left`,
        target: 'done',
      };
    }

    if (!hasMower) {
      if (cash < mowerPrice) {
        return {
          id: 'earn_for_mower',
          label: 'Earn enough for a mower',
          hint: 'Open your phone. A depot shift pays $60, or run courier gigs.',
          progress: Math.min(1, cash / mowerPrice),
          now: formatShort(cash),
          target: formatShort(mowerPrice),
        };
      }
      return {
        id: 'buy_mower',
        label: 'Buy a mower at the Tool Depot',
        hint: atLocation === 'toolshop'
          ? 'Press E to browse the equipment.'
          : 'It is the yellow building on the south side.',
        progress: 1,
        now: formatShort(cash),
        target: formatShort(mowerPrice),
      };
    }

    if (accepted.length === 0) {
      const open = s.leads.filter((l) => !l.accepted).length;
      return {
        id: 'accept_lead',
        label: 'Take a yard job',
        hint: open > 0
          ? 'Open your phone and accept a lead on the Work tab.'
          : 'No leads right now. Run a gig or work a shift while you wait.',
        progress: open > 0 ? 1 : 0,
        now: `${open} available`,
        target: 'accept one',
      };
    }

    const next = accepted[0]!;
    const here = atLocation === next.locationId;
    if (!s.business.registered) {
      const repNeeded = 8;
      if (s.statistics.jobsCompleted > 0 && s.player.reputation < repNeeded) {
        return {
          id: 'build_reputation',
          label: 'Build a reputation',
          hint: 'Finish jobs well and the neighbourhood starts calling you.',
          progress: Math.min(1, s.player.reputation / repNeeded),
          now: s.player.reputation.toFixed(0),
          target: String(repNeeded),
        };
      }
      if (s.player.reputation >= repNeeded && s.statistics.jobsCompleted > 0) {
        return {
          id: 'register',
          label: 'Register your business',
          hint: 'On the phone, under Business. It unlocks contracts and hiring.',
          progress: Math.min(1, cash / 12000),
          now: formatShort(cash),
          target: formatShort(12000),
        };
      }
    }

    if (s.business.registered && s.business.employees.length === 0) {
      const kitPrice = 29500;
      if (s.business.crewKits === 0) {
        return {
          id: 'buy_kit',
          label: 'Buy a second crew kit',
          hint: 'At the Tool Depot. You need one before anyone can work for you.',
          progress: Math.min(1, cash / kitPrice),
          now: formatShort(cash),
          target: formatShort(kitPrice),
        };
      }
      return {
        id: 'hire',
        label: 'Hire your first employee',
        hint: 'On the phone, under Business.',
        progress: 1,
        now: 'kit ready',
        target: 'hire',
      };
    }

    if (accepted.length > 0) {
      return {
        id: 'do_job',
        label: here ? `Start work at ${next.customerName}'s` : `Go to ${nameOfLocation(next.locationId)}`,
        hint: here ? 'Press E to start.' : 'Follow the marker above the property.',
        progress: here ? 1 : 0,
        now: `${accepted.length} booked`,
        target: 'do it',
      };
    }

    const milestone = this.sim.milestones.next(s);
    const netWorth = this.sim.netWorth();
    return {
      id: milestone?.id ?? 'goal',
      label: milestone?.label ?? 'Reach $10,000 net worth',
      hint: 'Keep taking work and reinvesting.',
      progress: Math.min(1, netWorth / BALANCE.netWorthGoal),
      now: formatShort(netWorth),
      target: formatShort(BALANCE.netWorthGoal),
    };
  }

  lastDaySummary(): DaySummary | null {
    return this.sim.getLastDaySummary();
  }

  // --- world / interaction ------------------------------------------------

  /** Locations that currently have work waiting, for map markers. */
  activeJobLocations(): Set<string> {
    return new Set(
      this.state.leads
        .filter((l) => l.accepted && !l.assignedEmployeeId)
        .map((l) => l.locationId),
    );
  }

  setPlayerPosition(x: number, z: number, facing: number, atLocationId: string | null): void {
    this.sim.setPlayerPosition(x, z, facing, atLocationId);
  }

  /** What pressing the interact key would do at this location right now. */
  interactionAt(locationId: string | null): InteractionView {
    if (!locationId) return { kind: 'none', locationId: '', label: '' };
    const s = this.state;
    const location: WorldLocation = getLocation(locationId);

    if (location.kind === 'home') return { kind: 'sleep', locationId, label: 'Sleep' };
    if (location.kind === 'toolshop') {
      return { kind: 'shop_tools', locationId, label: 'Browse equipment' };
    }
    if (location.kind === 'store') return { kind: 'shop_food', locationId, label: 'Buy food' };
    if (location.kind === 'bank') return { kind: 'bank', locationId, label: 'Bank' };
    if (location.kind === 'depot') return { kind: 'depot', locationId, label: 'Depot' };

    if (location.kind === 'customer') {
      const lead = s.leads.find(
        (l) => l.locationId === locationId && l.accepted && !l.assignedEmployeeId,
      );
      if (lead) {
        return { kind: 'job_site', locationId, label: 'Start work', leadId: lead.id };
      }
    }
    return { kind: 'none', locationId, label: '' };
  }

  // --- player actions -----------------------------------------------------
  //
  // Every mutating action runs `checkProgress` afterwards. Milestones are the
  // player's read on their own progress, and one that only appears on the next
  // clock tick reads as the game not noticing what they just did.

  private acted<T>(result: T): T {
    this.sim.checkProgress();
    return result;
  }

  acceptLead(id: string): boolean {
    return this.acted(this.sim.customers.acceptLead(this.state, id));
  }

  declineLead(id: string): boolean {
    return this.acted(this.sim.customers.declineLead(this.state, id, this.sim.reputation));
  }

  startWork(leadId: string): boolean {
    return this.acted(this.sim.startYardWork(leadId));
  }

  cancelWork(): void {
    this.sim.business.cancelWork(this.state);
  }

  completeGig(id: string): boolean {
    return this.acted(this.sim.jobs.completeCourierJob(this.state, id, this.sim.finance));
  }

  canWorkShift(): boolean {
    return this.sim.jobs.canWorkShift(this.state);
  }

  workShift(): { ok: boolean; pay: number } {
    const result = this.sim.jobs.workShift(this.state, this.sim.finance);
    if (result.ok) this.sim.advanceMinutes(result.minutes);
    return { ok: result.ok, pay: result.pay };
  }

  buyEquipment(id: string): boolean {
    if (id === EMPLOYEE_KIT.id) {
      return this.acted(this.sim.business.buyCrewKit(this.state, this.sim.finance));
    }
    return this.acted(this.sim.business.buyEquipment(this.state, id, this.sim.finance));
  }

  /**
   * Service a tool at the depot.
   *
   * Takes half an hour of the day as well as the money. Servicing has to cost
   * something the player feels beyond cash, or the decision collapses to
   * "always, immediately" and stops being one.
   */
  serviceEquipment(id: string): boolean {
    const done = this.sim.business.serviceEquipment(
      this.state, id, this.sim.finance,
    );
    if (done) this.sim.advanceMinutes(BALANCE.equipment.serviceMinutes);
    return this.acted(done);
  }

  /** Where the car is parked, or null if none is owned or none recorded yet. */
  vehicleParked(): { x: number; z: number; heading: number } | null {
    return this.state.player.vehicleParked;
  }

  setVehicleParked(x: number, z: number, heading: number): void {
    this.sim.property.setVehicleParked(this.state, x, z, heading);
  }

  /** Litres remaining, and the fraction of a tank that represents. */
  fuel(): { litres: number; fraction: number; empty: boolean } {
    const litres = this.state.player.fuel;
    const capacity = BALANCE.vehicle.fuelCapacity;
    return {
      litres,
      fraction: capacity > 0 ? litres / capacity : 0,
      empty: this.state.player.vehicleId !== null && litres <= 0.001,
    };
  }

  /** Burn fuel for metres driven. Returns true while there is still fuel. */
  drive(metres: number): boolean {
    this.sim.property.burnFuel(this.state, metres);
    return this.state.player.fuel > 0;
  }

  refuel(): boolean {
    return this.acted(this.sim.property.refuel(this.state, this.sim.finance));
  }

  buyVehicle(id: string): boolean {
    return this.acted(this.sim.property.buyVehicle(this.state, id, this.sim.finance));
  }

  moveHouse(id: string): boolean {
    return this.acted(this.sim.property.moveTo(this.state, id, this.sim.finance));
  }

  eat(id: string): boolean {
    return this.acted(this.sim.needs.eat(this.state, id, this.sim.finance));
  }

  registerBusiness(name: string): boolean {
    return this.acted(this.sim.business.registerBusiness(this.state, name, this.sim.finance));
  }

  hire(applicantId: string): boolean {
    const applicant = this.applicants().find((a) => a.id === applicantId);
    if (!applicant) return false;
    const hired = this.sim.employees.hire(this.state, applicant, this.sim.finance);
    if (hired) this.applicantCache = null;
    return this.acted(hired);
  }

  fire(id: string): boolean {
    return this.acted(this.sim.employees.fire(this.state, id));
  }

  deposit(amount: number): boolean {
    return this.acted(this.sim.finance.deposit(this.state, amount));
  }

  withdraw(amount: number): boolean {
    return this.acted(this.sim.finance.withdraw(this.state, amount));
  }

  takeLoan(amount: number): boolean {
    return this.acted(this.sim.finance.takeLoan(this.state, amount));
  }

  repayDebt(amount: number): boolean {
    return this.acted(this.sim.finance.repayDebt(this.state, amount));
  }

  maxLoan(): number {
    return Math.max(0, BALANCE.debt.maxLoan - this.state.finance.debt);
  }

  /**
   * Start a completely fresh game. Used by New Game on the title screen; a
   * stale simulation carried into a "new" game would be a nasty bug to find.
   */
  /**
   * Report how much of the property the player has actually worked.
   *
   * The presentation layer owns this: only the client knows where the mower
   * has been. The simulation assumes a job is done properly unless told
   * otherwise, so headless runs and employees are unaffected.
   */
  /** The world's static data for one location. */
  location(id: string): WorldLocation {
    return getLocation(id);
  }

  /**
   * Report how much of one task the player has done.
   *
   * Coverage is recomputed from the tasks rather than written directly, so
   * the client never has to know the weights. It stays at its default of 1
   * until something calls this: a headless run, a bot in the balance harness
   * and an employee working a contract all do the job properly by
   * definition, and only a player walking a yard can bring it down.
   */
  setTaskProgress(kind: WorkTaskKind, fraction: number): void {
    const work = this.state.player.activeWork;
    if (!work) return;
    const task = work.tasks.find((t) => t.kind === kind);
    if (!task) return;
    task.progress = Math.max(0, Math.min(1, fraction));
    work.coverage = BusinessSystem.coverageOf(work.tasks);
  }

  /** The tasks this job consists of, for the HUD. */
  workTasks(): { kind: WorkTaskKind; weight: number; progress: number }[] {
    return this.state.player.activeWork?.tasks.map((t) => ({ ...t })) ?? [];
  }

  setWorkCoverage(fraction: number): void {
    const work = this.state.player.activeWork;
    if (!work) return;
    work.coverage = Math.max(0, Math.min(1, fraction));
  }

  reset(seed?: number): void {
    this.sim = new Simulation(seed !== undefined ? { seed } : {});
    this.sim.bus.subscribe((e) => {
      for (const l of this.listeners) l(e);
    });
    this.applicantCache = null;
    this.applicantsForDay = -1;
  }

  // --- saves --------------------------------------------------------------

  private meta(slot: string): SaveMeta {
    const s = this.state;
    return {
      slot,
      day: s.time.day,
      clock: this.sim.time.formatClock(s),
      cash: s.finance.cash,
      netWorth: this.sim.netWorth(),
      businessName: s.business.registered ? s.business.name : 'Unregistered',
      reputation: s.player.reputation,
    };
  }

  save(slot: string): boolean {
    return this.saves.save(slot, this.state, this.meta(slot));
  }

  autosave(): boolean {
    return this.save(AUTOSAVE_SLOT);
  }

  load(slot: string): { ok: boolean; error?: string } {
    const result = this.saves.load(slot);
    if (!result.ok) return { ok: false, error: result.error };
    this.sim.restore(result.state);
    this.applicantCache = null;
    return { ok: true };
  }

  listSaves() {
    return this.saves.list();
  }

  deleteSave(slot: string): void {
    this.saves.remove(slot);
  }

  // --- debug tools --------------------------------------------------------

  /** Development only. Never reachable from normal gameplay. */
  readonly debug = {
    addMoney: (dollarsAmount: number) => {
      this.sim.finance.credit(this.state, Math.round(dollarsAmount * 100), 'debug', 'Debug: add money');
      this.sim.checkProgress();
    },
    removeMoney: (dollarsAmount: number) => {
      this.sim.finance.debit(this.state, Math.round(dollarsAmount * 100), 'debug', 'Debug: remove money');
    },
    addDebt: (dollarsAmount: number) => {
      this.state.finance.debt += Math.round(dollarsAmount * 100);
    },
    setHour: (hour: number) => {
      const s = this.state;
      const target = Math.max(0, Math.min(23, hour)) * 60;
      const delta = target > s.time.minuteOfDay
        ? target - s.time.minuteOfDay
        : BALANCE.time.minutesPerDay - s.time.minuteOfDay + target;
      this.sim.advanceMinutes(delta);
    },
    advanceDay: () => {
      this.sim.advanceMinutes(BALANCE.time.minutesPerDay);
    },
    setNeeds: (hunger: number, energy: number) => {
      const n = this.state.player.needs;
      n.hunger.value = Math.max(0, Math.min(100, hunger));
      n.energy.value = Math.max(0, Math.min(100, energy));
    },
    setWeather: (weather: Weather) => {
      this.state.weather = weather;
    },
    setReputation: (value: number) => {
      this.state.player.reputation = Math.max(0, Math.min(100, value));
    },
    spawnLead: () => {
      this.sim.customers.generateDailyLeads(this.state, 1);
    },
    completeActiveJob: () => {
      const work = this.state.player.activeWork;
      if (work) this.sim.advanceMinutes(Math.ceil(work.totalMinutes - work.elapsedMinutes));
    },
    giveEquipment: () => {
      for (const item of EQUIPMENT) {
        if (!this.state.business.equipment.includes(item.id)) {
          this.state.business.equipment.push(item.id);
        }
      }
      this.sim.checkProgress();
    },
    teleport: (locationId: string) => {
      this.travelTo(locationId);
    },
  };
}

/**
 * Plain-language tags for a customer's traits.
 *
 * Deliberately short and deliberately incomplete: only traits far enough from
 * average to change a decision are named, and never more than two, so a job
 * card stays readable. The thresholds are wide - a customer who is slightly
 * fussier than average is, for the player's purposes, just a customer.
 */
function leadTags(customer: Customer | undefined): string[] {
  if (!customer) return [];
  const t = traitsOf(customer);
  const tags: { text: string; weight: number }[] = [];
  if (t.difficulty > 0.78) tags.push({ text: 'Overgrown', weight: t.difficulty });
  else if (t.difficulty < 0.32) tags.push({ text: 'Easy lot', weight: 1 - t.difficulty });
  if (t.expectation > 0.8) tags.push({ text: 'Particular', weight: t.expectation });
  else if (t.expectation < 0.33) tags.push({ text: 'Easy-going', weight: 1 - t.expectation });
  if (t.patience < 0.4) tags.push({ text: 'In a hurry', weight: 1 - t.patience });
  if (t.generosity > 0.82) tags.push({ text: 'Tips well', weight: t.generosity });
  return tags.sort((a, b) => b.weight - a.weight).slice(0, 2).map((x) => x.text);
}
